import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type {
  AppProfile,
  CatalogueCandidate,
  CatalogueCandidateSource,
} from "../types";

function euro(value: number | null | undefined) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function pct(value: number | null | undefined) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(Number(value)) + "%";
}

type Props = {
  profile: AppProfile;
};

type PromotionDraft = {
  code: string;
  name: string;
  referenceId: string;
};

export default function CatalogueCandidates({ profile }: Props) {
  const [status, setStatus] = useState("pending");
  const [minProjects, setMinProjects] = useState(2);
  const [stableOnly, setStableOnly] = useState(true);
  const [rows, setRows] = useState<CatalogueCandidate[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sources, setSources] = useState<Record<string, CatalogueCandidateSource[]>>({});
  const [sourceBusy, setSourceBusy] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [drafts, setDrafts] = useState<Record<string, PromotionDraft>>({});

  const canReview = profile.role === "admin" || profile.role === "estimator";

  async function load() {
    setBusy(true);
    setError("");
    setNotice("");

    let query = supabase
      .from("catalogue_candidates")
      .select(
        "id,group_key,representative_description,original_unit,observation_count,project_count,min_dry_unit_price,median_dry_unit_price,avg_dry_unit_price,max_dry_unit_price,price_spread_pct,latest_year,status,review_note,reviewed_at",
      )
      .eq("status", status)
      .eq("is_current", true)
      .gte("project_count", minProjects)
      .order("project_count", { ascending: false })
      .order("observation_count", { ascending: false })
      .limit(100);

    if (stableOnly) {
      query = query.lte("price_spread_pct", 25);
    }

    const { data, error: queryError } = await query;

    if (queryError) {
      setError(queryError.message);
      setRows([]);
    } else {
      setRows((data ?? []) as CatalogueCandidate[]);
    }

    setBusy(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, minProjects, stableOnly]);

  async function toggleSources(candidate: CatalogueCandidate) {
    if (expanded === candidate.id) {
      setExpanded(null);
      return;
    }

    setExpanded(candidate.id);
    if (sources[candidate.id]) return;

    setSourceBusy(candidate.id);
    const { data, error: sourceError } = await supabase.rpc(
      "get_catalogue_candidate_sources",
      {
        candidate_id: candidate.id,
        result_limit: 30,
      },
    );

    if (sourceError) {
      setError(sourceError.message);
    } else {
      setSources((current) => ({
        ...current,
        [candidate.id]: (data ?? []) as CatalogueCandidateSource[],
      }));
    }
    setSourceBusy(null);
  }

  function updateDraft(candidateId: string, patch: Partial<PromotionDraft>) {
    setDrafts((current) => ({
      ...current,
      [candidateId]: {
        code: current[candidateId]?.code ?? "",
        name: current[candidateId]?.name ?? "",
        referenceId: current[candidateId]?.referenceId ?? "",
        ...patch,
      },
    }));
  }

  async function promote(candidate: CatalogueCandidate) {
    if (!canReview) return;

    const draft = drafts[candidate.id] ?? { code: "", name: "", referenceId: "" };
    if (!draft.code.trim() || !draft.name.trim() || !draft.referenceId) {
      setError("Indique código, nome do artigo e a linha histórica que ficará como referência.");
      return;
    }

    setActionBusy(candidate.id);
    setError("");
    setNotice("");

    const { data, error: promoteError } = await supabase.rpc(
      "promote_catalogue_candidate",
      {
        candidate_id: candidate.id,
        work_code: draft.code.trim(),
        work_name: draft.name.trim(),
        reference_import_line_id: draft.referenceId,
        work_description: candidate.representative_description,
        work_notes: null,
      },
    );

    if (promoteError) {
      setError(promoteError.message);
    } else {
      setNotice(
        `Artigo ${draft.code.trim()} criado no catálogo com a referência histórica selecionada.`,
      );
      setRows((current) => current.filter((row) => row.id !== candidate.id));
      setExpanded(null);
      setDrafts((current) => {
        const next = { ...current };
        delete next[candidate.id];
        return next;
      });
      console.info("Created work item", data);
    }

    setActionBusy(null);
  }

  async function review(candidate: CatalogueCandidate, nextStatus: string) {
    if (!canReview) return;
    setActionBusy(candidate.id);
    setError("");

    const { error: reviewError } = await supabase.rpc(
      "review_catalogue_candidate",
      {
        candidate_id: candidate.id,
        next_status: nextStatus,
        note: null,
      },
    );

    if (reviewError) {
      setError(reviewError.message);
    } else {
      setRows((current) => current.filter((row) => row.id !== candidate.id));
      setExpanded((current) => (current === candidate.id ? null : current));
    }

    setActionBusy(null);
  }

  return (
    <section className="stack-xl">
      <header className="page-header">
        <div>
          <p className="eyebrow">Normalização assistida</p>
          <h2>Candidatos a artigo L37</h2>
          <p className="muted">
            São descrições que aparecem repetidamente em obras diferentes. Nenhuma
            delas entra no catálogo nem define um preço de referência sem revisão
            humana.
          </p>
        </div>
      </header>

      <div className="candidate-filters card">
        <label>
          <span>Estado</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="pending">Por rever</option>
            <option value="shortlist">Guardados para catálogo</option>
            <option value="rejected">Rejeitados</option>
          </select>
        </label>

        <label>
          <span>Mínimo de obras</span>
          <select
            value={minProjects}
            onChange={(event) => setMinProjects(Number(event.target.value))}
          >
            <option value={2}>2 obras</option>
            <option value={3}>3 obras</option>
            <option value={4}>4 obras</option>
            <option value={5}>5 obras</option>
          </select>
        </label>

        <label className="check-field">
          <input
            type="checkbox"
            checked={stableOnly}
            onChange={(event) => setStableOnly(event.target.checked)}
          />
          <span>Mostrar apenas variação de preço ≤ 25%</span>
        </label>

        <button className="button button-secondary" type="button" onClick={() => void load()}>
          Atualizar
        </button>
      </div>

      <div className="candidate-summary">
        <strong>{busy ? "…" : rows.length.toLocaleString("pt-PT")}</strong>
        <span>candidatos nesta vista</span>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}
      {busy ? <div className="empty card">A carregar candidatos…</div> : null}

      {!busy && !error && rows.length === 0 ? (
        <div className="empty card">
          <strong>Sem candidatos para estes filtros.</strong>
          <span>Altere o estado, o número mínimo de obras ou a estabilidade do preço.</span>
        </div>
      ) : null}

      <div className="result-list">
        {rows.map((candidate) => {
          const candidateSources = sources[candidate.id] ?? [];
          const isExpanded = expanded === candidate.id;

          return (
            <article className="candidate-card card" key={candidate.id}>
              <div className="candidate-main">
                <div className="result-meta">
                  <span className="pill">{candidate.original_unit || "—"}</span>
                  <span>{candidate.project_count} proposta(s)/obra(s)</span>
                  <span>{candidate.observation_count} observ.</span>
                  {candidate.latest_year ? <span>{candidate.latest_year}</span> : null}
                  <span>variação {pct(candidate.price_spread_pct)}</span>
                </div>

                <h3>{candidate.representative_description}</h3>

                <div className="candidate-prices">
                  <div>
                    <span>Mínimo</span>
                    <b>{euro(candidate.min_dry_unit_price)}</b>
                  </div>
                  <div className="candidate-median">
                    <span>Mediana PU seco</span>
                    <b>{euro(candidate.median_dry_unit_price)}</b>
                  </div>
                  <div>
                    <span>Máximo</span>
                    <b>{euro(candidate.max_dry_unit_price)}</b>
                  </div>
                </div>

                <div className="candidate-actions">
                  <button
                    className="button button-secondary"
                    type="button"
                    onClick={() => void toggleSources(candidate)}
                  >
                    {isExpanded ? "Fechar histórico" : "Ver histórico"}
                  </button>

                  {canReview && status !== "shortlist" ? (
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={actionBusy === candidate.id}
                      onClick={() => void review(candidate, "shortlist")}
                    >
                      Guardar para catálogo
                    </button>
                  ) : null}

                  {canReview && status !== "rejected" ? (
                    <button
                      className="button button-danger"
                      type="button"
                      disabled={actionBusy === candidate.id}
                      onClick={() => void review(candidate, "rejected")}
                    >
                      Rejeitar
                    </button>
                  ) : null}

                  {canReview && status !== "pending" ? (
                    <button
                      className="text-action"
                      type="button"
                      disabled={actionBusy === candidate.id}
                      onClick={() => void review(candidate, "pending")}
                    >
                      Voltar a por rever
                    </button>
                  ) : null}
                </div>
              </div>

              {isExpanded ? (
                <div className="candidate-history">
                  <h4>Observações históricas desta descrição</h4>
                  {sourceBusy === candidate.id ? (
                    <div className="muted">A carregar histórico…</div>
                  ) : (
                    <>
                      <div
                        className={
                          status === "shortlist"
                            ? "candidate-history-table candidate-history-selectable"
                            : "candidate-history-table"
                        }
                      >
                        <div className="candidate-history-row candidate-history-head">
                          <span>Ano</span>
                          <span>Obra</span>
                          <span>PU seco</span>
                          <span>Coef.</span>
                          <span>PU venda</span>
                          {status === "shortlist" ? <span>Referência</span> : null}
                        </div>
                        {candidateSources.map((source) => (
                          <div className="candidate-history-row" key={source.import_line_id}>
                            <span>{source.proposal_year ?? "—"}</span>
                            <span>{source.project_name || source.source_title || "—"}</span>
                            <strong>{euro(source.dry_unit_price)}</strong>
                            <span>
                              {source.commercial_coefficient
                                ? Number(source.commercial_coefficient).toLocaleString("pt-PT", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : "—"}
                            </span>
                            <span>{euro(source.sale_unit_price)}</span>
                            {status === "shortlist" ? (
                              <label className="reference-choice">
                                <input
                                  type="radio"
                                  name={`reference-${candidate.id}`}
                                  checked={
                                    (drafts[candidate.id]?.referenceId ?? "") ===
                                    source.import_line_id
                                  }
                                  onChange={() =>
                                    updateDraft(candidate.id, {
                                      referenceId: source.import_line_id,
                                    })
                                  }
                                />
                                <span>Usar</span>
                              </label>
                            ) : null}
                          </div>
                        ))}
                      </div>

                      {status === "shortlist" && canReview ? (
                        <div className="promotion-panel">
                          <div>
                            <p className="eyebrow">Criar catálogo</p>
                            <h4>Promover para artigo L37</h4>
                            <p className="muted">
                              O preço de referência inicial será exatamente a linha histórica
                              escolhida acima. A mediana não é aprovada automaticamente.
                            </p>
                          </div>

                          <div className="promotion-fields">
                            <label>
                              <span>Código L37</span>
                              <input
                                type="text"
                                placeholder="Ex.: L37-ALV-001"
                                value={drafts[candidate.id]?.code ?? ""}
                                onChange={(event) =>
                                  updateDraft(candidate.id, { code: event.target.value })
                                }
                              />
                            </label>
                            <label className="grow">
                              <span>Nome normalizado do trabalho</span>
                              <input
                                type="text"
                                placeholder="Ex.: Alvenaria de tijolo cerâmico 11 cm"
                                value={drafts[candidate.id]?.name ?? ""}
                                onChange={(event) =>
                                  updateDraft(candidate.id, { name: event.target.value })
                                }
                              />
                            </label>
                            <button
                              className="button button-primary"
                              type="button"
                              disabled={
                                actionBusy === candidate.id ||
                                !(drafts[candidate.id]?.code ?? "").trim() ||
                                !(drafts[candidate.id]?.name ?? "").trim() ||
                                !(drafts[candidate.id]?.referenceId ?? "")
                              }
                              onClick={() => void promote(candidate)}
                            >
                              {actionBusy === candidate.id
                                ? "A criar…"
                                : "Criar artigo L37"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}
